import type { ReactNode, SVGProps } from 'react';
import type { DropdownMenuCheckboxItemProps } from '@radix-ui/react-dropdown-menu';
import { DropdownMenuCheckboxItem, DropdownMenuItemIndicator } from '@radix-ui/react-dropdown-menu';
import { isNotNil } from '@wistia/type-guards';
import type { MenuItemButtonProps } from './MenuItemButton';
import { MenuItemButton } from './MenuItemButton';
import { Icon } from '../Icon';

export type CheckboxIndicatorProps = SVGProps<SVGSVGElement> & {
  checked: boolean;
};

const CheckboxIndicator = ({ checked, ...props }: CheckboxIndicatorProps) => {
  return checked ? (
    <svg
      {...props}
      fill="none"
      height="24"
      viewBox="0 0 24 24"
      width="24"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="m4 8c0-2.20914 1.79086-4 4-4h8c2.2091 0 4 1.79086 4 4v8c0 2.2091-1.7909 4-4 4h-8c-2.20914 0-4-1.7909-4-4z"
        fill="#2949e5"
      />
      <path
        d="m10.4728 15.1931-3.09523-3.1131c-.18596-.187-.18596-.4902 0-.6773l.67341-.6773c.18596-.187.48749-.187.67344 0l2.08508 2.0971 4.4661-4.49174c.1859-.18703.4875-.18703.6734 0l.6734.67731c.186.18703.186.49027 0 .67731l-5.4762 5.50772c-.1859.187-.4874.187-.6734 0z"
        fill="#fff"
      />
    </svg>
  ) : (
    <svg
      {...props}
      fill="none"
      height="24"
      viewBox="0 0 24 24"
      width="24"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="m8 4.5h8c1.933 0 3.5 1.567 3.5 3.5v8c0 1.933-1.567 3.5-3.5 3.5h-8c-1.933 0-3.5-1.567-3.5-3.5v-8c0-1.933 1.567-3.5 3.5-3.5z"
        fill="#fcfcfd"
      />
      <path
        d="m8 4.5h8c1.933 0 3.5 1.567 3.5 3.5v8c0 1.933-1.567 3.5-3.5 3.5h-8c-1.933 0-3.5-1.567-3.5-3.5v-8c0-1.933 1.567-3.5 3.5-3.5z"
        stroke="#bbbbc4"
      />
    </svg>
  );
};

export type CheckboxMenuItemProps = DropdownMenuCheckboxItemProps &
  MenuItemButtonProps & {
    /** Callback to fire when the checked status changes */
    onCheckedChange: (checked: boolean) => void;
    /**
     * Optional `Icon` to use as the indicator that the checkbox item is selected. Must be an instance of [Icon](../?path=/docs/components-icon--docs)
     */
    indicator?: ReactNode;
    /**
     * The  checked state of the menu item
     */
    checked: boolean;
    /**
     * @ignore
     */
    href?: never;
    /**
     * @ignore
     */
    isLoading?: never;

    /**
     * @ignore
     */
    ref?: never;
  };

export const CheckboxMenuItem = ({
  onSelect = () => null,
  checked,
  onCheckedChange,

  ...props
}: CheckboxMenuItemProps) => {
  return (
    <DropdownMenuCheckboxItem
      asChild={true}
      checked={checked}
      onCheckedChange={onCheckedChange}
      onSelect={onSelect}
    >
      <MenuItemButton
        {...props}
        leftIcon={isNotNil(props.icon) ? props.icon : <CheckboxIndicator checked={checked} />}
        rightIcon={
          isNotNil(props.icon) ? (
            <DropdownMenuItemIndicator>
              <Icon type="checkmark" />
            </DropdownMenuItemIndicator>
          ) : undefined
        }
      />
    </DropdownMenuCheckboxItem>
  );
};

CheckboxMenuItem.displayName = 'CheckboxMenuItem';
